import { Link } from 'react-router-dom';
import PolicyLayout, {
  PolicyContact,
  PolicyList,
  PolicySection,
  PolicySubsection,
} from '../components/PolicyLayout';
import {
  COMPANY_NAME,
  SITE_DOMAIN,
  SITE_NAME,
  SITE_URL,
} from '../constants/site';

const UPDATED = 'May 24, 2026';

export default function CookiePolicyPage() {
  return (
    <PolicyLayout title="Cookie Policy" lastUpdated={UPDATED}>
      <PolicySection title="1. Introduction">
        <p>
          This Cookie Policy explains how {SITE_NAME} ({SITE_URL}, &ldquo;{SITE_DOMAIN}&rdquo;) operated by {COMPANY_NAME}
          uses cookies, pixels, SDKs, and similar tracking technologies when you browse our website, search travel
          options, or engage with our communications.
        </p>
        <p>
          For broader details on how we process personal information, please see our{' '}
          <Link to="/flight/privacy">Privacy Policy</Link>.
        </p>
      </PolicySection>

      <PolicySection title="2. What cookies and similar technologies are">
        <p>
          Cookies are small text files placed on your device by websites you visit. Similar technologies include local
          storage objects, tracking pixels, and software development kits (SDKs) that help us remember preferences,
          improve security, and analyze website performance.
        </p>
      </PolicySection>

      <PolicySection title="3. Types of cookies we use">
        <PolicyList
          items={[
            'Strictly necessary cookies — required for core functionality such as session management, fraud prevention, secure checkout, and load balancing.',
            'Functional cookies — remember settings like language, currency, region, and recent search preferences.',
            'Performance and analytics cookies — measure visits, traffic sources, and interaction behavior so we can optimize content and performance.',
            'Advertising and targeting cookies — help deliver relevant promotions, limit repeated ads, and measure campaign effectiveness.',
          ]}
        />
      </PolicySection>

      <PolicySection title="4. Why we use these technologies">
        <p>We use cookies and similar tools to:</p>
        <PolicyList
          items={[
            'Keep the website secure and operational.',
            'Maintain your session and booking flow state.',
            'Remember your preferences for a smoother experience.',
            'Measure traffic patterns and improve usability.',
            'Personalize offers and marketing, where allowed by law and your choices.',
          ]}
        />
      </PolicySection>

      <PolicySection title="5. Third-party cookies and tools">
        <p>
          Some cookies are set by third parties that provide services to us, such as analytics providers, ad networks,
          payment processors, and travel technology partners. These providers may process data under their own privacy
          policies and cookie notices.
        </p>
        <p>
          We encourage you to review third-party privacy disclosures when interacting with embedded widgets or linked
          services.
        </p>
      </PolicySection>

      <PolicySection title="6. Cookie duration">
        <PolicySubsection title="6.1 Session cookies">
          <p>Session cookies are temporary and expire when you close your browser.</p>
        </PolicySubsection>
        <PolicySubsection title="6.2 Persistent cookies">
          <p>
            Persistent cookies remain on your device until they expire automatically or are deleted by you. Retention
            periods vary based on cookie purpose and provider settings.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="7. Managing your cookie preferences">
        <p>
          You can control cookies through browser settings and, where available, cookie preference tools presented on
          our website.
        </p>
        <PolicyList
          items={[
            'You can delete existing cookies and block future cookies through browser controls.',
            'Blocking strictly necessary cookies may prevent key site functions such as booking and secure sign-in.',
            'You can opt out of some analytics/advertising cookies via applicable consent tools and industry opt-out programs.',
          ]}
        />
      </PolicySection>

      <PolicySection title="8. Do Not Track and regional rights">
        <p>
          Some browsers offer a &ldquo;Do Not Track&rdquo; signal. Because there is no universally accepted standard,
          our site may not respond to all such signals consistently. Depending on your jurisdiction, you may have rights
          to opt out of targeted advertising and certain tracking activities.
        </p>
      </PolicySection>

      <PolicySection title="9. Changes to this Cookie Policy">
        <p>
          We may update this Cookie Policy from time to time to reflect legal, technical, or business developments.
          The &ldquo;Last updated&rdquo; date indicates when this policy was last revised.
        </p>
      </PolicySection>

      <PolicySection title="10. Contact us">
        <p>For cookie-related questions or requests, contact us at:</p>
        <PolicyContact />
        <p>
          You may also review our <Link to="/flight/terms">Terms &amp; Conditions</Link> for additional information on use of
          the services.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
